import { LibraryPattern } from '_types/Pattern/Library';
import { CategoryShape } from '_types/StoreFront/Category';
import { PatternLibraryBrowseGroup, PatternLibrarySearchHit } from './types';

/** Web speaks `no | en`; the catalogue speaks `nb | en`. */
export const toLocale = (language: LanguageCode): 'nb' | 'en' =>
    language === 'no' ? 'nb' : 'en';

/** Comma-joins arrays, drops undefined and empty values. */
export const toQuery = (
    params: Record<string, string | number | string[] | undefined>,
): string => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) {
            return;
        }
        const str = Array.isArray(value) ? value.join(',') : String(value);
        if (str !== '') {
            query.set(key, str);
        }
    });
    return query.toString();
};

/**
 * Favorites in Laravel are keyed by Magento's SKU. A hit exposes both the
 * current and the legacy sku; prefer the legacy one so stored favorites
 * keep matching. A miss would have the client auto-delete the favorite.
 */
export const patternKeyOf = (pattern: {
    legacy_sku?: string | null;
    sku: string;
}): string => pattern.legacy_sku ?? pattern.sku;

/** PDF language codes → the display names the Magento-era card shape carried. */
const LANGUAGE_NAMES: Record<string, Record<LanguageCode, string>> = {
    nb: { no: 'Norsk', en: 'Norwegian' },
    no: { no: 'Norsk', en: 'Norwegian' },
    en: { no: 'Engelsk', en: 'English' },
    da: { no: 'Dansk', en: 'Danish' },
    sv: { no: 'Svensk', en: 'Swedish' },
    de: { no: 'Tysk', en: 'German' },
    fi: { no: 'Finsk', en: 'Finnish' },
    is: { no: 'Islandsk', en: 'Icelandic' },
    nl: { no: 'Nederlandsk', en: 'Dutch' },
};

export const patternLanguageLabel = (
    code: string,
    language: LanguageCode,
): string =>
    LANGUAGE_NAMES[code.toLowerCase()]?.[language] ?? code.toUpperCase();

/**
 * Catalogue images are full-size originals (200 KB to 6 MB); the app resizes
 * them through a Bunny pull zone per catalogue host and so do we. Width is in
 * device pixels. Any other URL (relative, proxied, a local catalogue) is left
 * alone, since no pull zone fetches from it.
 *
 * This used to go through Cloudinary's fetch CDN. That account is closed and
 * answers 401 for every image it has not already cached.
 */
const BUNNY_PULL_ZONES: Record<string, string> = {
    'admin.patterns.knitandnote.com': 'kn-patterns.b-cdn.net',
    'admin.patterns.stage.knitandnote.com': 'kn-patterns-stage.b-cdn.net',
};

export const cdnImageUrl = (url: string, width: number): string => {
    const match = /^https:\/\/([^/]+)(\/.*)$/.exec(url);
    const pullZone = match && BUNNY_PULL_ZONES[match[1]];
    if (!match || !pullZone) {
        return url;
    }
    const path = match[2];
    const separator = path.includes('?') ? '&' : '?';
    return `https://${pullZone}${path}${separator}width=${width}&quality=80&format=webp&optimizer=image`;
};

/** 2x the widest a picker card gets (calc(50% - 9px) of a 500px list). */
const CARD_IMAGE_WIDTH = 512;

/** Search hit → the LibraryPattern card shape PatternChoiceItem renders. */
export const toPatternCardItem = (
    hit: PatternLibrarySearchHit,
    language: LanguageCode,
): LibraryPattern => ({
    id: 0,
    sku: patternKeyOf(hit),
    name: language === 'no' ? hit.name_nb : hit.name_en,
    languages: hit.languages.map((code, id) => ({
        id,
        label: patternLanguageLabel(code, language),
    })),
    designer: hit.designer_name
        ? [{ name: hit.designer_name, code: hit.designer_slug ?? '' }]
        : [],
    thumbnail: hit.primary_image_url
        ? { url: cdnImageUrl(hit.primary_image_url, CARD_IMAGE_WIDTH) }
        : undefined,
});

/**
 * The browse cards flattened, in the server's display order. Every card
 * arrives whole, so nothing here resolves a key against another list.
 */
export const toBrowseCategoryCards = (
    browseGroups: PatternLibraryBrowseGroup[] | undefined,
): CategoryShape[] =>
    (browseGroups ?? []).flatMap((group) =>
        group.categories.map((category) => ({
            key: category.key,
            name: category.label,
            image: category.iconUrl ?? undefined,
        })),
    );
